import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import { Settings, X, Lightbulb, BarChart2, Zap } from 'lucide-react';

import showToaster from '../../../helpers/toasterMessages/showToaster';
import s from './TopUpModal.css';

const UPDATE_COMMISSION_RATE = gql`
  mutation updateFeaturedCommissionRate(
    $featuredListingId: Int!
    $commissionRate: Float!
  ) {
    updateFeaturedCommissionRate(
      featuredListingId: $featuredListingId
      commissionRate: $commissionRate
    ) {
      status
      newCommissionRate
      newPosition
      errorMessage
    }
  }
`;

const GET_FEATURED_COMPETITORS = gql`
  query getFeaturedCompetitors($listId: Int!, $proposedRate: Float) {
    getFeaturedCompetitors(listId: $listId, proposedRate: $proposedRate) {
      status
      currentPosition
      totalCompetitors
      rateToBeFirst
      competitors {
        position
        listTitle
        commissionRate
      }
    }
  }
`;

class TopUpModal extends Component {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    featuredListingId: PropTypes.number.isRequired,
    listId: PropTypes.number,
    listTitle: PropTypes.string,
    currentCommissionRate: PropTypes.number
  };

  static defaultProps = {
    currentCommissionRate: 0,
    listTitle: ''
  };

  constructor(props) {
    super(props);
    this.state = {
      newRate: props.currentCommissionRate || 0,
      isLoading: false,
      error: null,
      competitorData: null
    };
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.loadCompetitorData();
      this.setState({ newRate: this.props.currentCommissionRate || 0 });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.setState({ 
        newRate: this.props.currentCommissionRate || 0,
        error: null
      });
      this.loadCompetitorData();
    }
    if (this.props.isOpen && prevProps.currentCommissionRate !== this.props.currentCommissionRate) {
      this.setState({ newRate: this.props.currentCommissionRate || 0 });
    }
  }

  loadCompetitorData = async () => {
    const { client, listId, currentCommissionRate } = this.props;
    if (!listId) return;
    try {
      const { data } = await client.query({
        query: GET_FEATURED_COMPETITORS,
        variables: { listId, proposedRate: currentCommissionRate || 0 },
        fetchPolicy: 'network-only'
      });
      if (data?.getFeaturedCompetitors?.status === 'success') {
        this.setState({ competitorData: data.getFeaturedCompetitors });
      }
    } catch (error) {
      console.error('Error loading competitor data:', error);
    }
  };

  handleSliderChange = (e) => {
    const value = parseFloat(e.target.value);
    this.setState({ newRate: value }, () => this.updateEstimatedPosition(value));
  };

  handleInputChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    const clampedValue = Math.max(0, Math.min(40, value));
    this.setState({ newRate: clampedValue }, () => this.updateEstimatedPosition(clampedValue));
  };

  updateEstimatedPosition = async (rate) => {
    const { client, listId } = this.props;
    if (!listId) return;
    try {
      const { data } = await client.query({
        query: GET_FEATURED_COMPETITORS,
        variables: { listId, proposedRate: rate },
        fetchPolicy: 'network-only'
      });
      if (data?.getFeaturedCompetitors?.status === 'success') {
        this.setState({ competitorData: data.getFeaturedCompetitors });
      }
    } catch (error) {
      console.error('Error updating position:', error);
    }
  };

  handleSubmit = async () => {
    const { client, featuredListingId, onSuccess, onClose } = this.props;
    const { newRate } = this.state;

    this.setState({ isLoading: true, error: null });

    try {
      const { data } = await client.mutate({
        mutation: UPDATE_COMMISSION_RATE,
        variables: {
          featuredListingId,
          commissionRate: newRate
        }
      });

      if (data?.updateFeaturedCommissionRate?.status === 'success') {
        showToaster({ 
          messageId: 'success', 
          toasterType: 'success', 
          requestMessage: `¡Comisión actualizada a ${newRate}%!`
        });
        onSuccess && onSuccess();
        onClose();
      } else {
        this.setState({ 
          error: data?.updateFeaturedCommissionRate?.errorMessage || 'Error al modificar comisión',
          isLoading: false 
        });
      }
    } catch (error) {
      this.setState({ error: error.message, isLoading: false });
    }
  };

  render() {
    const { isOpen, onClose, listTitle, currentCommissionRate } = this.props;
    const { newRate, isLoading, error, competitorData } = this.state;

    if (!isOpen) return null;

    const hasChanged = newRate !== (currentCommissionRate || 0);
    const estimatedPosition = competitorData?.currentPosition;
    const rateToBeFirst = competitorData?.rateToBeFirst;

    return (
      <div className={s.overlay} onClick={onClose}>
        <div className={s.modal} onClick={e => e.stopPropagation()}>
          <div className={s.header}>
            <h2 className={s.title}>
              <Settings size={20} /> Modificar Comisión
            </h2>
            <button className={s.closeBtn} onClick={onClose}><X size={20} /></button>
          </div>

          <div className={s.content}>
            {error && <div className={s.error}>{error}</div>}

            {/* Explicación de Comisión */}
            <div className={s.bidExplanation}>
              <div className={s.bidExplanationIcon}><Lightbulb size={24} /></div>
              <div className={s.bidExplanationText}>
                <strong>¿Cómo funciona la Comisión?</strong>
                <p>Ajusta tu % de comisión para mejorar tu posición. 
                Entre mayor comisión ofrezcas, mejor posición tendrás.
                <strong> Solo se cobra cuando hay reservas.</strong></p>
              </div>
            </div>

            <div className={s.listingInfo}>
              <h3 className={s.listingTitle}>{listTitle}</h3>
              <p className={s.currentBid}>Comisión actual: {(currentCommissionRate || 0).toFixed(1)}%</p>
            </div>

            <div className={s.section}>
              <h4 className={s.sectionTitle}>Nueva Comisión</h4>
              
              {/* Slider */}
              <div className={s.sliderContainer}>
                <input
                  type="range"
                  className={s.bidSlider}
                  min="0"
                  max="40"
                  step="1"
                  value={newRate}
                  onChange={this.handleSliderChange}
                />
                <div className={s.sliderLabels}>
                  <span>0%</span>
                  <span>20%</span>
                  <span>40%</span>
                </div>
              </div>

              {/* Manual Input */}
              <div className={s.customInput}>
                <span>Porcentaje:</span>
                <input
                  type="number"
                  min="0"
                  max="40"
                  step="1"
                  value={newRate}
                  onChange={this.handleInputChange}
                />
                <span>%</span>
              </div>

              {rateToBeFirst && rateToBeFirst > newRate && (
                <div className={s.suggestion}>
                  <Lightbulb size={16} />
                  <span className={s.suggestionText}>
                    Con {Math.ceil(rateToBeFirst)}% alcanzarías la posición #1
                  </span>
                </div>
              )}
            </div>

            <div className={s.section}>
              <h4 className={s.sectionTitle}>Resumen</h4>
              <div className={s.summary}>
                <div className={s.summaryRow}>
                  <span>Comisión actual</span>
                  <span>{(currentCommissionRate || 0).toFixed(1)}%</span>
                </div>
                <div className={s.summaryRow}>
                  <span>Cambio</span>
                  <span style={{ color: newRate > currentCommissionRate ? '#059669' : newRate < currentCommissionRate ? '#DC2626' : '#6B7280' }}>
                    {newRate > currentCommissionRate ? '+' : ''}{(newRate - (currentCommissionRate || 0)).toFixed(1)}%
                  </span>
                </div>
                <div className={cx(s.summaryRow, s.summaryTotal)}>
                  <span>Nueva comisión</span>
                  <span>{newRate.toFixed(1)}%</span>
                </div>
              </div>

              {estimatedPosition && (
                <div className={s.positionEstimate}>
                  <BarChart2 size={16} /> Posición estimada: #{estimatedPosition}
                </div>
              )}
            </div>
          </div>

          <div className={s.footer}>
            <button className={s.cancelBtn} onClick={onClose}>
              Cancelar
            </button>
            <button 
              className={s.submitBtn} 
              onClick={this.handleSubmit}
              disabled={isLoading || !hasChanged}
            >
              {isLoading ? 'Procesando...' : <><Zap size={16} /> Guardar Comisión</>}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default compose(
  withStyles(s),
  withApollo,
  connect()
)(TopUpModal);
